import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scheme, SchemeListResponse } from '../types/client';
import { schemesApi } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { useTableState } from '@/hooks/useTableState';
import { ActiveStatus, RowActionMenu } from '@/components/ui';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';

interface SchemeListProps {
  onSchemeSelect?: (scheme: Scheme) => void;
}

const SchemeList: React.FC<SchemeListProps> = ({ onSchemeSelect }) => {
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const [schemes, setSchemes] = useState<Scheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    currentPage, pageSize, ordering, searchQuery, debouncedSearch,
    setTotalCount, onSort, onSearchChange, onPageChange,
    totalCount, totalPages, sortField, sortDir,
  } = useTableState({ defaultSortField: 'display_name', searchDebounceMs: 400 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
        ordering,
      };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const response: SchemeListResponse = await schemesApi.getSchemes(params);
      setSchemes(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error('Error fetching schemes:', err);
      setError('Failed to fetch schemes');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, ordering, debouncedSearch, setTotalCount]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo((): ColumnDef<Scheme>[] => [
    { key: 'display_name', header: 'Name', sortable: true, render: r => r.display_name },
    { key: 'code', header: 'Code', sortable: true, render: r => r.code || 'N/A' },
    { key: 'acronym', header: 'Acronym', sortable: true, render: r => r.acronym || '—' },
    { key: 'full_name', header: 'Full Name', sortable: true, render: r => r.full_name || '—' },
    { key: 'fa_scheme_id', header: 'FA Scheme ID', render: r => r.fa_scheme_id || 'N/A' },
    // Not sortable — fa_source is not in the scheme endpoint's ordering_fields.
    { key: 'fa_source', header: 'FA Source', render: r => r.fa_source_display || r.fa_source || '—' },
    { key: 'investment_strategy__name', header: 'Investment Strategy', sortable: true, render: r => r.investment_strategy_display || 'N/A' },
    { key: 'created_ts', header: 'Created', sortable: true, render: r => formatDate(r.created_ts) },
    {
      key: 'is_active', header: 'Status', render: r => <ActiveStatus active={!!r.is_active} />,
    },
    {
      key: 'actions', header: 'Actions', width: '90px', render: r => (
        <RowActionMenu
          label={`Actions for ${r.code || r.name}`}
          actions={[
            { label: 'View Details', icon: <VisibilityIcon fontSize="small" />, onClick: () => (onSchemeSelect ? onSchemeSelect(r) : navigate(`/schemes/${r.id}`)) },
            { label: 'Edit', icon: <EditIcon fontSize="small" />, hidden: !canWrite, onClick: () => navigate(`/schemes/${r.id}/edit`) },
          ]}
        />
      ),
    },
  ], [navigate, onSchemeSelect, canWrite]);

  return (
    <DataTable
      title="Investment Schemes"
      data={schemes}
      columns={columns}
      rowKey={r => r.id}
      totalCount={totalCount}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={pageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by scheme name or code..."
      rowStyle={r => (!r.is_active ? { opacity: 0.7, backgroundColor: 'var(--surface-2)' } : undefined)}
      emptyMessage="No schemes found."
    />
  );
};

export default SchemeList;